import { FC, useEffect, useMemo } from 'react';
import { useMount } from 'react-use';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';
import isEmpty from 'lodash/isEmpty';

import { getFileMimeFromName, getFileTypeFromName } from '@cvt/helpers/file';

type Props = {
  open: boolean;
  onClose: () => void;
  options?: { files?: (File | string)[] };
};

const isQuickTime = (mime?: string) =>
  mime === 'video/quicktime' || mime === 'video/mov';

const makeVideoSlide = (src: string, mime?: string) => ({
  type: 'video' as const,
  sources: isQuickTime(mime) ? [{ src }] : [{ src, type: mime }],
  poster: '',
});

const emptyList: Array<File | string> = [];

export const MediaLightboxDialog: FC<Props> = ({ open, onClose, options }) => {
  const files = options?.files ?? emptyList;
  const { slides, objectUrls } = useMemo(() => {
    const urlsToRevoke: string[] = [];

    const slides = files.map((file) => {
      if (typeof file === 'string') {
        const type = getFileTypeFromName(file);
        if (type === 'video') {
          const mime = getFileMimeFromName(file);
          // @ts-ignore
          return makeVideoSlide(file, mime);
        }
        return { src: file };
      }
      const url = URL.createObjectURL(file);
      urlsToRevoke.push(url);

      const type = getFileTypeFromName(file.name);
      if (type === 'video') {
        return makeVideoSlide(url, file.type);
      }
      return { src: url };
    });

    return { slides, objectUrls: urlsToRevoke };
  }, [files]);

  useMount(() => {
    if (isEmpty(files)) onClose();
  });

  useEffect(() => {
    return () => objectUrls.forEach((u) => URL.revokeObjectURL(u));
  }, [objectUrls]);

  if (isEmpty(slides)) return null;


  return (
    <Lightbox
      open={open}
      close={onClose}
      // @ts-ignore
      slides={slides}
      plugins={[Video]}
      video={{ autoPlay: true, controls: true, loop: true }}
    />
  );
};
